import React, { useState, useRef, useEffect } from 'react';
import styles from './M_LanguageSwitcher.module.css';
import A_ListItem from '@/components/A_Atoms/A_ListItem/A_ListItem';
import cn from 'classnames';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { useLanguage } from '@/contexts/LanguageContext';

export default function M_LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { language, setLanguage } = useLanguage();

  const languages: { code: 'ru' | 'en' | 'fr'; label: string }[] = [
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'ENG' },
    { code: 'fr', label: 'FR' },
  ];

  const handleLanguageSelect = (languageCode: 'ru' | 'en' | 'fr') => {
    setLanguage(languageCode);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleToggle();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type='button'
        className={cn(styles.selected_wrapper, { [styles.active]: isOpen })}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-labelledby='language-label'
      >
        <Q_Icon.LanguageIcon width={24} height={24} fill='none' />
        <span id='language-label' className={cn(styles.label, 'text-p-3')}>
          {currentLanguage?.label}
        </span>
        <div
          className={cn(styles.arrow_wrapper, { [styles.arrow_up]: isOpen })}
        >
          <Q_Icon.DropdownArrowIcon width={12} height={6} fill='none' />
        </div>
      </button>

      {isOpen && (
        <div
          className={styles.list_wrapper}
          role='listbox'
          aria-label='Выбор языка'
        >
          <div className={styles.list_plate}></div>
          {languages.map(languageOption => (
            <div key={languageOption.code} className={styles.list_item}>
              <A_ListItem
                label={languageOption.label}
                active={languageOption.code === language}
                onClick={() => handleLanguageSelect(languageOption.code)}
                role='option'
                aria-selected={languageOption.code === language}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
